import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetAllMaterialRelatedData } from '../../../store/data/Functions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import MaterialHttpService from '../../../services/http/masterData/materialHttpService';
import MaterialRelatedDataHttpService from '../../../services/http/masterData/materialRelatedDataHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import MaterialForm from './form/MaterialForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { setMaterialsMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { dataSetMaterials } from '../../../store/masterData/Actions';
import { IApiError } from '../../../@types/other';
import { useLocation } from 'react-router-dom';
import MaterialModelHelper from '../../../@types/model/masterData/material/materialModelHelper';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import { IMaterial } from '../../../@types/model/masterData/material/material';
import { IMaterialFormValues } from '../../../@types/model/masterData/material/materialFormValues';

const MaterialScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const materials = useAppSelector(x => x.masterData.materials);
    const packs = useAppSelector(x => x.masterData.packs);
    const palletBaseTypes = useAppSelector(x => x.masterData.palletBaseTypes);
    const materialTypes = useAppSelector(x => x.masterData.materialTypes);
    const unitOfMeasures = useAppSelector(x => x.masterData.unitOfMeasures);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<IMaterial | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingMaterial, setDeletingMaterial] = useState<IMaterial | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                try {
                    await syncMasterData(false);
                } catch (error : unknown) {
                    if (isMounted) {
                        const apiError = error as IApiError;
                        generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while syncing master data.');
                    }
                }
            } else {
                setIsLoading(true);
                try {
                    const res = await MaterialRelatedDataHttpService.getAllMaterialRelatedMasterData();
                    if (isMounted) {
                        dataSetAllMaterialRelatedData(res.data);
                    }
                } catch (error : unknown) {
                    if (isMounted) {
                        const apiError = error as IApiError;
                        generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading material related data.');
                    }
                } finally {
                    if (isMounted) {
                        setIsLoading(false);
                    }
                }
            }
        };
        fetchData();
        return () => {
            isMounted = false;
        };
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = useCallback(async (values : IMaterialFormValues) => {
        setIsLoading(true);

        try {
            const upsert = MaterialModelHelper.createUpsert(values);
            const res = await MaterialHttpService.addOrUpdateMaterial(upsert);
            const updatedList = upsertArrayElement(materials, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetMaterials(updatedList));
            await setMaterialsMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedMaterial ? 'Material updated successfully.' : 'Material added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating material data.');
        } finally {
            setIsLoading(false);
        }
    }, [materials, selectedMaterial]);

    const removeMaterial = useCallback(async () => {
        if (!deletingMaterial) return;
        const updatedMaterial = { ...deletingMaterial, isActive: false };
        setIsLoading(true);
        try {
            const res = await MaterialHttpService.deleteMaterial(updatedMaterial.id);
            const updatedList = upsertArrayElement(materials, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetMaterials(updatedList));
            await setMaterialsMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Material deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting material.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    }, [deletingMaterial, materials]);

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedMaterial(null);
    };

    const edit = (selectedRow : IMaterial) => {
        setSelectedMaterial(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IMaterial) : void => {
        setDeletingMaterial(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingMaterial(null);
    };

    const deleteButtonTooltip = (row : IMaterial) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Material', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatPacks = useCallback((packIds : Array<number>) => {
        return packs
            ?.filter(x => packIds?.some(y => y === x.id))
            ?.map(x => x.code)
            .join(', ') ?? '';
    }, [packs]);

    const getMaterialTypeName = useCallback((materialTypeId : number) => {
        const materialType = materialTypes?.find(x => x.id === materialTypeId);
        return materialType?.name ?? '';
    }, [materialTypes]);

    const getPalletBaseTypeName = useCallback((palletBaseTypeId : number) => {
        const palletBaseType = palletBaseTypes?.find(x => x.id === palletBaseTypeId);
        return palletBaseType?.name ?? '';
    }, [palletBaseTypes]);

    const getUnitOfMeasureName = useCallback((unitOfMeasureId : number) => {
        const unitOfMeasure = unitOfMeasures?.find(x => x.id === unitOfMeasureId);
        return unitOfMeasure?.name ?? '';
    }, [unitOfMeasures]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return MaterialModelHelper.createFormValues(
                selectedMaterial,
                materialTypes,
                palletBaseTypes,
                unitOfMeasures,
                packs
            );
        }
    }, [selectedMaterial, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <Card className='fdc hfill'>
                <CustomTable<IMaterial>
                    
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Material', RightGrantsEnum.Add) : 'Add'}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Material', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IMaterial) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableSorting
                    enableFiltering
                    enablePagination
                    fitWidthToPage
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Material Type', field: 'materialTypeId', formatFunction: getMaterialTypeName, enableFiltering: true, enableSorting: true },
                        { title: 'Pallet Base Type', field: 'palletBaseTypeId', formatFunction: getPalletBaseTypeName, enableFiltering: true, enableSorting: true },
                        { title: 'Packs', field: 'packIds', formatFunction: formatPacks, enableFiltering: true, enableSorting: true },
                        { title: 'Unit Of Measure', field: 'unitOfMeasureId', formatFunction: getUnitOfMeasureName, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={materials}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={190}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedMaterial ? 'Edit Material' : 'Add Material'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <MaterialForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Material'}
                open={!!deletingMaterial}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeMaterial}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default MaterialScreen;
